import React from 'react';
import {
  getLabelsForStep,
  stepKeyFromMedia,
  getRequiredSteps,
  stepKeyToRole,
} from '../utils/submissionHelpers';

type MediaRole = 'primary' | 'expiration_label' | 'after';

interface LabelListPanelProps {
  labels: any[];
  submission: any;
  activeRole: string;
  onFlagClick: (index: number, label: any) => void;
  getCategoryName: (categoryId: string) => string;
  getCategoryDescription?: (categoryId: string) => string;
  variant: 'review' | 'qa';
  showRoleBreakdown?: boolean;
  hideWhenEmpty?: boolean;
  selectedIndex?: number | null;
  onSelect?: (index: number) => void;
}

function isMultiPhotoSubmission(submission: any): boolean {
  return Array.isArray(submission?.media) && (submission?.media?.length ?? 0) >= 2;
}

function getRequiredRoles(submission: any): MediaRole[] {
  if (!submission) return ['primary'];

  const stepKeys = getRequiredSteps(submission);
  const roles: MediaRole[] = [];
  for (const stepKey of stepKeys) {
    const role = stepKeyToRole(stepKey, submission);
    if (role && !roles.includes(role)) {
      roles.push(role);
    }
  }

  if (roles.length === 0) {
    return ['primary'];
  }

  return roles;
}

function getRoleDisplayLabel(role: MediaRole, submission?: any): string {
  if (role === 'primary') {
    if (submission?.media?.some((m: any) => m.role === 'after')) {
      return 'Before';
    }
    return 'Primary';
  }
  if (role === 'expiration_label') return 'Expiration Label';
  if (role === 'after') return 'After';
  return role;
}

export default function LabelListPanel({
  labels,
  submission,
  activeRole,
  onFlagClick,
  getCategoryName,
  getCategoryDescription,
  variant,
  showRoleBreakdown = false,
  hideWhenEmpty = false,
  selectedIndex = null,
  onSelect,
}: LabelListPanelProps) {
  if (hideWhenEmpty && (!labels || labels.length === 0)) {
    return null;
  }

  const isMultiPhoto = isMultiPhotoSubmission(submission);
  const role = activeRole as MediaRole;

  const wrapperClass =
    variant === 'review'
      ? 'p-4 border-b border-gray-200'
      : 'mb-4 p-3 bg-gray-50 rounded';

  const listMaxHeightClass = variant === 'review' ? 'max-h-40' : 'max-h-60';
  const nonFlaggedRowBg = variant === 'review' ? 'bg-gray-50' : 'bg-white';

  return (
    <div className={wrapperClass}>
      {showRoleBreakdown && isMultiPhoto ? (
        <div className="mb-2">
          <h3 className="font-semibold">
            Labels - {getRoleDisplayLabel(role, submission)} ({labels.length})
          </h3>
          <div className="text-xs text-gray-500 mt-1">
            {getRequiredRoles(submission).map((r) => (
              <span key={r} className="mr-3">
                {getRoleDisplayLabel(r, submission)}:{' '}
                {(() => {
                  const mediaItem = submission?.media?.find((m: any) => m.role === r);
                  const stepKey = stepKeyFromMedia(mediaItem);
                  return getLabelsForStep(submission, stepKey).length;
                })()}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <h3 className="font-semibold mb-2">
          Labels (
          {variant === 'review'
            ? submission?.labels?.length || 0
            : labels.length}
          )
          {!showRoleBreakdown && isMultiPhoto && (
            <span className="text-xs text-gray-500 ml-2">
              - {getRoleDisplayLabel(role, submission)}
            </span>
          )}
        </h3>
      )}

      <div className={`space-y-2 ${listMaxHeightClass} overflow-auto`}>
        {labels.map((label, index) => {
          const categoryName = getCategoryName(label.categoryId);
          const categoryDescription = getCategoryDescription
            ? getCategoryDescription(label.categoryId)
            : '';

          const isFlagged = label.flagged === true;
          const flagReasonText = label.flagReason || '';

          return (
            <div
              key={index}
              onClick={() => onSelect?.(index)}
              className={`flex items-center justify-between p-2 rounded border ${
                onSelect ? 'cursor-pointer' : ''
              } ${
                isFlagged ? 'bg-yellow-50 border-yellow-300' : `${nonFlaggedRowBg} border-gray-200`
              } ${selectedIndex === index ? 'ring-2 ring-blue-400' : ''}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium truncate" title={categoryName}>
                    {categoryName}
                  </div>
                  {isFlagged && (
                    <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">
                      Flagged
                    </span>
                  )}
                </div>
                {categoryDescription && (
                  <div className="text-xs text-gray-400 truncate" title={categoryDescription}>
                    {categoryDescription}
                  </div>
                )}
                {isFlagged && flagReasonText && (
                  <div className="text-xs text-yellow-700 mt-1 font-medium">
                    Reason: {flagReasonText}
                  </div>
                )}
                {variant === 'review' ? (
                  <div className="text-xs text-gray-500">
                    {label.bbox[2] - label.bbox[0]} × {label.bbox[3] - label.bbox[1]} px
                  </div>
                ) : (
                  label.bbox &&
                  Array.isArray(label.bbox) &&
                  label.bbox.length === 4 && (
                    <div className="text-xs text-gray-500">
                      {label.bbox[2] - label.bbox[0]} × {label.bbox[3] - label.bbox[1]} px
                    </div>
                  )
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFlagClick(index, label);
                }}
                className={`px-2 py-1 text-xs rounded ${
                  isFlagged
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                }`}
                title={
                  isFlagged
                    ? `Update flag for ${categoryName}`
                    : `Flag ${categoryName} for review`
                }
              >
                {isFlagged ? 'Update Flag' : 'Flag'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
